// CrmCommentModal.tsx
import React, {useEffect, useState} from 'react';
import {Button, Center, Checkbox, Flex, Grid, Loader, Modal, Select, Textarea, TextInput} from '@mantine/core';
import {IconBell, IconCalendarPlus, IconPencil} from '@tabler/icons-react';
import {UseFormReturnType} from '@mantine/form';
import {useTranslation} from 'react-i18next';
import {LanguageUtils} from '@bobex/react-core/utils/LanguageUtils';
import {TimeInput} from '@mantine/dates';
import BMDateInput from '@bobex/react-core/components/BMDateInput';
import {DateUtils} from '@bobex/react-core/utils/DateUtils';

interface CRMCommentModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: any) => Promise<void>;
  form: UseFormReturnType<any>;
  crmCategories: { value: string; label: string }[];
  crmStatus: { value: string; label: string }[];
  isEditing: boolean;
}

export default function CRMCommentModal({
  opened,
  onClose,
  onSubmit,
  form,
  crmCategories,
  crmStatus,
  isEditing,
}: CRMCommentModalProps) {
  const { t, i18n } = useTranslation();
    const [dateValue, setDateValue] = useState<Date | null>(null);

    useEffect(() => {
        setDateValue(form.values.dateDue ? new Date(form.values.dateDue) : null);
    }, [opened]);

  // Only render the form once the category/status options have loaded — an uncontrolled Select mounted
  // with empty data drops its default value, which posts an invalid (0) id.
  const optionsReady = crmCategories.length > 0 && crmStatus.length > 0;

  return (
    <Modal
      title={isEditing ? t('Update comment') : t('Create new comment')}
      opened={opened}
      onClose={onClose}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="lg"
      centered
    >
      {!optionsReady ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Grid gap="md">
          <Grid.Col span={12}>
            <TextInput
              label={t('Subject')}
              placeholder={t('Subject')}
              withAsterisk
              {...form.getInputProps('title')}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <BMDateInput
              label={t('Reminder date')}
              placeholder={t('Reminder date')}
              locale={LanguageUtils.changeToBCP47(i18n.language)}
              valueFormat={DateUtils.getFormat()}
              dateParser={DateUtils.getDateParser()}
              clearable
              value={dateValue}
              onChange={(val) => {
                  const date = val ? new Date(val) : null;
                  setDateValue(date);
                  form.setFieldValue('dateDue', date);
              }}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TimeInput
              label={t('Reminder time')}
              placeholder={t('Select time')}
              withSeconds={false}
              {...form.getInputProps('timeDue')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Checkbox
              label={
                <Flex align="center" gap="xs">
                  <IconBell size={16} />
                  <span>{t('Enable reminder')}</span>
                </Flex>
              }
              {...form.getInputProps('reminderStatus', { type: 'checkbox' })}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Checkbox
              label={
                <Flex align="center" gap="xs">
                  <IconCalendarPlus size={16} />
                  <span>{t('Add to my Google Calendar')}</span>
                </Flex>
              }
              {...form.getInputProps('addToGoogleCalendar', { type: 'checkbox' })}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <Select
              label={t('Category')}
              placeholder={t('Category')}
              {...form.getInputProps('categoryId')}
              data={crmCategories}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <Select
              label={t('Status')}
              placeholder={t('Status')}
              {...form.getInputProps('statusId')}
              data={crmStatus}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label={t('Comment 1')}
              placeholder={t('Comments')}
              autosize
              minRows={3}
              {...form.getInputProps('problemDesc')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label={t('Comment 2')}
              placeholder={t('Comments')}
              autosize
              minRows={3}
              {...form.getInputProps('solution')}
            />
          </Grid.Col>
        </Grid>

        <Flex justify="flex-end" gap="md" mt="md">
          <Button type="submit">
            <IconPencil stroke={2} />
            {t('Save')}
          </Button>
        </Flex>
      </form>
      )}
    </Modal>
  );
}

